/**
 * Webhook Timeline page (introduced Day 3 — replay-defense badge
 * added in Phase F polish sprint EMU.2).
 *
 * Subscribes to the backend SSE stream at `/api/webhooks` and renders
 * a vertical timeline of every incoming webhook delivery, newest at
 * the top. Each entry shows:
 *
 *   - HMAC verdict badge — ✓ green / ✗ red / "—" muted (no secret yet)
 *   - Replay-defense badge — ⤴ amber "duplicate (first seen #N)" when
 *     the bridge's replay ledger detected this `event_id` as a repeat
 *     of a previously-seen body (Phase 1.1 wire surface). Replays are
 *     STILL rendered in the timeline for forensics; the badge is the
 *     "skip me" hint for downstream business consumers. Original entry
 *     id is linkable: `firstSeenEntryId` cross-references back.
 *   - event_type + event_id + receivedAt
 *   - expandable payload viewer (JSON pretty-print)
 *   - expandable signature inspector — received vs expected
 *
 * The page renders all data the bridge captures; nothing is hidden
 * behind a "load more" wall — that would obscure the real "did my
 * payload show up?" question. Ring buffer cap = 1000 on the backend,
 * so the in-DOM list is bounded too.
 *
 * Connection state banner: explicit "connecting / live / lost" line
 * at the top. Without it a silent SSE drop (e.g. backend restart)
 * is indistinguishable from "no events yet" — and the dev tool's
 * whole purpose is to make that distinction loud.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { WebhookEntry } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WebhookHmacValidator } from '@/components/WebhookHmacValidator';

type ConnState = 'connecting' | 'open' | 'lost';

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function shortId(id: string | null): string {
  if (id === null) return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function hmacBadge(hmacValid: boolean | null) {
  if (hmacValid === true) {
    return (
      <Badge variant="success" title="HMAC signature matched expected">
        ✓ HMAC valid
      </Badge>
    );
  }
  if (hmacValid === false) {
    return (
      <Badge variant="danger" title="HMAC signature did NOT match expected">
        ✗ HMAC invalid
      </Badge>
    );
  }
  return (
    <Badge variant="muted" title="No shared secret configured yet (auto-subscribe may have failed)">
      — unsigned
    </Badge>
  );
}

interface DuplicateBadgeProps {
  readonly duplicate: boolean;
  readonly firstSeenEntryId: number | null;
  readonly onJumpToOriginal: (id: number) => void;
}

function DuplicateBadge({ duplicate, firstSeenEntryId, onJumpToOriginal }: DuplicateBadgeProps) {
  if (!duplicate) {
    return null;
  }
  const title =
    firstSeenEntryId === null
      ? 'Backend flagged this delivery as a replay (same event_id seen before)'
      : `Replay of entry #${firstSeenEntryId} — same event_id, body recorded for forensics but downstream consumers should skip business processing. Click to jump to the original.`;
  const label =
    firstSeenEntryId === null ? '⤴ duplicate' : `⤴ duplicate (first seen #${firstSeenEntryId})`;

  if (firstSeenEntryId === null) {
    return (
      <Badge variant="warning" title={title}>
        {label}
      </Badge>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onJumpToOriginal(firstSeenEntryId)}
      className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100 hover:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300 cursor-pointer"
      title={title}
    >
      {label}
    </button>
  );
}

interface SignatureDetailsProps {
  readonly entry: WebhookEntry;
}

function SignatureDetails({ entry }: SignatureDetailsProps) {
  return (
    <div className="space-y-1 text-xs font-mono">
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <span className="text-slate-500">received:</span>
        <span
          className={
            entry.receivedSignature === null
              ? 'text-slate-400 italic'
              : entry.hmacValid === false
                ? 'text-red-700 break-all'
                : 'text-slate-800 break-all'
          }
        >
          {entry.receivedSignature ?? '(no X-Nikira-Signature header)'}
        </span>
      </div>
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <span className="text-slate-500">expected:</span>
        <span
          className={
            entry.expectedSignature === null ? 'text-slate-400 italic' : 'text-slate-800 break-all'
          }
        >
          {entry.expectedSignature ?? '(no secret configured)'}
        </span>
      </div>
      {Object.keys(entry.inspectedHeaders).length > 0 && (
        <div className="pt-1 mt-1 border-t border-slate-200">
          <div className="text-slate-500 mb-1">request headers:</div>
          <div className="space-y-0.5 pl-2">
            {Object.entries(entry.inspectedHeaders).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[140px_1fr] gap-2">
                <span className="text-slate-400">{k}</span>
                <span className="text-slate-700 break-all">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface EntryCardProps {
  readonly entry: WebhookEntry;
  readonly onJumpToOriginal: (id: number) => void;
  readonly highlighted: boolean;
}

function EntryCard({ entry, onJumpToOriginal, highlighted }: EntryCardProps) {
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const eventLabel = entry.eventType ?? '(unparsed)';
  const borderClass = highlighted
    ? 'border-amber-300 ring-2 ring-amber-200 transition-shadow'
    : entry.hmacValid === false
      ? 'border-red-200 ring-1 ring-red-100'
      : entry.duplicate
        ? 'border-amber-200'
        : 'border-slate-200';
  return (
    <Card id={`webhook-entry-${entry.id}`} className={borderClass}>
      <CardHeader className="flex items-center gap-2 flex-wrap">
        <CardTitle className="font-mono text-sm flex items-center gap-2">
          <span className="text-slate-400">#{entry.id}</span>
          <span>{eventLabel}</span>
        </CardTitle>
        {hmacBadge(entry.hmacValid)}
        <DuplicateBadge
          duplicate={entry.duplicate}
          firstSeenEntryId={entry.firstSeenEntryId}
          onJumpToOriginal={onJumpToOriginal}
        />
        <Badge variant="muted" title={`event_id: ${entry.eventId ?? 'unparsed'}`}>
          evt {shortId(entry.eventId)}
        </Badge>
        <div className="flex-1" />
        <time className="text-xs font-mono text-slate-500" dateTime={entry.receivedAt}>
          {entry.receivedAt}
        </time>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPayloadOpen((v) => !v)}
            aria-expanded={payloadOpen}
          >
            {payloadOpen ? '▾ Hide payload' : '▸ Show payload'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSigOpen((v) => !v)}
            aria-expanded={sigOpen}
          >
            {sigOpen ? '▾ Hide signature' : '▸ Show signature'}
          </Button>
        </div>
        {payloadOpen && (
          <pre className="bg-slate-900 text-slate-100 rounded-md p-3 text-xs font-mono overflow-x-auto max-h-[320px] overflow-y-auto">
            {prettyJson(entry.parsedBody ?? entry.rawBody)}
          </pre>
        )}
        {sigOpen && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <SignatureDetails entry={entry} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ConnBannerProps {
  readonly state: ConnState;
  readonly entryCount: number;
  readonly onReconnect: () => void;
}

function ConnBanner({ state, entryCount, onReconnect }: ConnBannerProps) {
  if (state === 'connecting') {
    return (
      <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
        Connecting to <code className="font-mono">/api/webhooks</code> …
      </div>
    );
  }
  if (state === 'lost') {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900 flex items-center gap-3">
        <span className="flex-1">
          <strong>SSE connection lost.</strong> The browser will auto-retry, but the live feed may
          be paused. Check the emulator backend is running on the same port.
        </span>
        <Button size="sm" variant="secondary" onClick={onReconnect}>
          Reconnect now
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
        live
      </span>
      <span className="text-slate-300">·</span>
      <span>{entryCount} entries</span>
    </div>
  );
}

export function WebhookTimeline() {
  const [entries, setEntries] = useState<WebhookEntry[]>([]);
  const [conn, setConn] = useState<ConnState>('connecting');
  const [reconnectTick, setReconnectTick] = useState(0);
  const [highlightedEntryId, setHighlightedEntryId] = useState<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  const jumpToOriginal = (id: number) => {
    const el = document.getElementById(`webhook-entry-${id}`);
    if (el !== null) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedEntryId(id);
      window.setTimeout(() => {
        setHighlightedEntryId((curr) => (curr === id ? null : curr));
      }, 1800);
    }
  };

  useEffect(() => {
    setConn('connecting');
    const es = new EventSource('/api/webhooks');
    sourceRef.current = es;

    const onWebhook = (e: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(e.data) as WebhookEntry;
        setEntries((prev) => [parsed, ...prev]);
      } catch {
        // malformed frame — ignore (defensive only; the bridge always JSON.stringify's)
      }
    };
    const onSnapshot = (e: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(e.data) as WebhookEntry;
        // Snapshot frames arrive in chronological order (oldest first
        // in the bridge), so prepend each to keep newest-on-top in
        // the UI. We can't sort by id after-the-fact because future
        // live frames will keep arriving and we'd thrash.
        setEntries((prev) => [parsed, ...prev]);
      } catch {
        /* see comment in onWebhook */
      }
    };

    es.addEventListener('webhook', onWebhook as EventListener);
    es.addEventListener('snapshot', onSnapshot as EventListener);
    es.onopen = () => setConn('open');
    es.onerror = () => {
      // EventSource auto-reconnects unless we explicitly close. Set
      // "lost" so the user sees the banner; "connecting" / "open"
      // will take over again on successful reconnect.
      setConn((curr) => (curr === 'open' ? 'lost' : curr));
    };

    return () => {
      es.removeEventListener('webhook', onWebhook as EventListener);
      es.removeEventListener('snapshot', onSnapshot as EventListener);
      es.close();
      sourceRef.current = null;
    };
  }, [reconnectTick]);

  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => b.id - a.id);
  }, [entries]);

  const duplicateCount = useMemo(() => entries.filter((e) => e.duplicate).length, [entries]);

  return (
    <div className="grid grid-cols-[1fr_360px] h-[calc(100vh-92px)] min-h-0">
      <main className="overflow-y-auto p-6 space-y-4 bg-slate-50">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-lg font-semibold text-slate-900">Webhook Timeline</h2>
          <ConnBanner
            state={conn}
            entryCount={sorted.length}
            onReconnect={() => setReconnectTick((t) => t + 1)}
          />
          {duplicateCount > 0 && (
            <Badge
              variant="warning"
              title={`${duplicateCount} replay${duplicateCount === 1 ? '' : 's'} detected by event_id ledger`}
            >
              ⤴ {duplicateCount} replay{duplicateCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
        {conn === 'lost' && (
          <ConnBanner
            state="lost"
            entryCount={sorted.length}
            onReconnect={() => setReconnectTick((t) => t + 1)}
          />
        )}
        {sorted.length === 0 && conn === 'open' ? (
          <Card>
            <CardContent>
              <p className="text-sm text-slate-500">
                No webhook deliveries yet. Trigger a mutating endpoint from the{' '}
                <strong>Endpoint Explorer</strong> tab (e.g. <code>admin_mint</code>) and events
                will appear here within a couple of seconds — assuming the emulator auto-subscribed
                on startup. If the timeline stays empty for a long time, check the backend log for{' '}
                <code>Subscribed to Gateway: subscription_id=…</code>— without that line, Gateway is
                not configured to call back.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sorted.map((e) => (
              <EntryCard
                key={e.id}
                entry={e}
                onJumpToOriginal={jumpToOriginal}
                highlighted={highlightedEntryId === e.id}
              />
            ))}
          </div>
        )}
      </main>
      <aside className="border-l border-slate-200 bg-white overflow-y-auto p-4 space-y-3 min-h-0">
        <h3 className="text-sm font-semibold text-slate-900">HMAC validator</h3>
        <p className="text-xs text-slate-500">
          Paste a raw body + shared secret to compute the expected{' '}
          <code className="font-mono">X-Nikira-Signature</code>. Optional: paste a received
          signature to get a ✓/✗ verdict.
        </p>
        <WebhookHmacValidator />
      </aside>
    </div>
  );
}
